/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { ShieldCheck, Heart, Mail, ExternalLink, RefreshCw } from 'lucide-react';

export function Footer() {
  const year = new Date().getFullYear();

  const handleNav = (path: string, e: React.MouseEvent) => {
    e.preventDefault();
    window.history.pushState(null, '', path);
    window.dispatchEvent(new Event('popstate'));
  };

  return (
    <footer className="mt-16 border-t border-slate-100 bg-slate-900 text-slate-300 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pb-8 border-b border-slate-800">
          
          {/* Brand Info */}
          <div>
            <a 
              href="/"
              onClick={(e) => {
                if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
                  e.preventDefault();
                  window.history.pushState(null, '', '/');
                  window.dispatchEvent(new Event('popstate'));
                }
              }}
              className="flex items-center space-x-2 text-white hover:text-slate-200 mb-4 inline-flex"
              id="footer-logo-container"
            >
              <div className="bg-[#004a99] text-white px-2 py-1 rounded font-extrabold text-sm leading-none shadow-sm transition hover:bg-blue-800 shrink-0">
                SR
              </div>
              <div className="flex flex-col text-left">
                <span className="font-display text-xs sm:text-sm font-extrabold tracking-tight text-white uppercase leading-none">
                  smart-rechnr<span className="text-[#3b82f6]">.de</span>
                </span>
                <span className="text-[8px] uppercase tracking-widest text-[#3b82f6] font-bold mt-0.5 leading-none">
                  Professionelle Online-Rechner
                </span>
              </div>
            </a>
            <p className="text-sm text-slate-300 leading-relaxed max-w-sm">
              {"Ihre zuverl\u00E4ssige Plattform f\u00FCr pr\u00E4zise Steuern- und Finanzberechnungen in Deutschland. Alle Rechner sind an die Gesetzesgrundlagen f\u00FCr die Abrechnungsjahre 2025 und 2026 angepasst."}
            </p>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col space-y-2">
            <div className="text-white font-medium text-sm mb-2">{"Die 5 beliebtesten Online-Rechner"}</div>
            <div className="flex flex-col gap-y-2 text-sm text-slate-300">
              <a href="/brutto-netto" onClick={(e) => handleNav('/brutto-netto', e)} className="hover:text-white transition cursor-pointer">✓ Brutto-Netto-Rechner</a>
              <a href="/lohnsteuerrechner" onClick={(e) => handleNav('/lohnsteuerrechner', e)} className="hover:text-white transition cursor-pointer">✓ Lohnsteuer-Rechner</a>
              <a href="/einkommensteuer" onClick={(e) => handleNav('/einkommensteuer', e)} className="hover:text-white transition cursor-pointer">✓ Einkommensteuer-Rechner</a>
              <a href="/buergergeld-rechner" onClick={(e) => handleNav('/buergergeld-rechner', e)} className="hover:text-white transition cursor-pointer">✓ Bürgergeld-Rechner</a>
              <a href="/wohngeldrechner" onClick={(e) => handleNav('/wohngeldrechner', e)} className="hover:text-white transition cursor-pointer">✓ Wohngeldrechner 2026</a>
            </div>
          </div>

          {/* Trust and Updates */}
          <div>
            <div className="text-white font-medium text-sm mb-2">{"Pr\u00E4zision & Aktualit\u00E4t"}</div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-2 text-green-400 font-medium font-bold">
                <ShieldCheck className="h-4 w-4 shrink-0 text-green-400" />
                <span>{"SSL-verschl\u00FCsselt & DSGVO-konform"}</span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                {"Unsere Rechner verwenden zertifizierte Steuer- und Finanzformeln. Ohne Cookies, ohne Tracking, ohne externe API-Verbindungen f\u00FCr Berechnungen - 100% anonym, sicher und lokal."}
              </p>
            </div>
          </div>

        </div>

        {/* Bottom footer with Professional Polish Metadata */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-8 text-xs text-slate-300 gap-4">
          <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
            <span>&copy; {year} <span className="text-white font-bold tracking-tight">SMART-RECHNR<span className="text-[#3b82f6]">.DE</span></span>. Alle Rechte vorbehalten.</span>
            <span className="text-slate-600" aria-hidden="true">|</span>
            <a 
              href="/impressum" 
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState(null, '', '/impressum');
                window.dispatchEvent(new Event('popstate'));
              }}
              className="hover:text-white transition"
            >
              Impressum
            </a>
            <span className="text-slate-600" aria-hidden="true">|</span>
            <a 
              href="/datenschutz" 
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState(null, '', '/datenschutz');
                window.dispatchEvent(new Event('popstate'));
              }}
              className="hover:text-white transition"
            >
              Datenschutz
            </a>
            <span className="text-slate-600" aria-hidden="true">|</span>
            <a 
              href="/haftungsausschluss" 
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState(null, '', '/haftungsausschluss');
                window.dispatchEvent(new Event('popstate'));
              }}
              className="hover:text-white transition"
            >
              Haftungsausschluss
            </a>
            <span className="text-slate-600" aria-hidden="true">|</span>
            <a 
              href="/kontakt" 
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState(null, '', '/kontakt');
                window.dispatchEvent(new Event('popstate'));
              }}
              className="hover:text-white transition"
            >
              Kontakt
            </a>
            <span className="text-slate-600" aria-hidden="true">|</span>
            <a 
              href="/rechner-einbinden" 
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState(null, '', '/rechner-einbinden');
                window.dispatchEvent(new Event('popstate'));
              }}
              className="hover:text-white transition text-blue-300 font-semibold"
            >
              Rechner einbinden
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-slate-300">
            <span className="text-slate-400">Wissenschaftliche Redaktion: <strong className="text-slate-200">Ahmed</strong> (M.Sc. Computer Science &amp; Mathematics)</span>
            <div className="flex items-center space-x-2 pl-2 border-l border-slate-700">
              {/* Facebook circular icon */}
              <a
                href="https://web.facebook.com/smartrechnr/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 rounded-full bg-slate-800 hover:bg-[#1877f2] text-slate-300 hover:text-white flex items-center justify-center transition shadow-xs border border-slate-700 hover:border-[#1877f2]"
                title="Smart Rechner auf Facebook"
                aria-label="Smart Rechner auf Facebook"
              >
                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>

              {/* Twitter / X circular icon */}
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="w-7 h-7 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white flex items-center justify-center transition shadow-xs border border-slate-700"
                title="Smart Rechner auf Twitter / X"
                aria-label="Smart Rechner auf Twitter / X"
              >
                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>

              {/* YouTube circular icon */}
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="w-7 h-7 rounded-full bg-slate-800 hover:bg-[#ff0000] text-slate-300 hover:text-white flex items-center justify-center transition shadow-xs border border-slate-700 hover:border-[#ff0000]"
                title="Smart Rechner auf YouTube"
                aria-label="Smart Rechner auf YouTube"
              >
                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
